/**
 * Control Presupuestal del Proyecto
 * Namespace de clases: tuviCP-*
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import client from '../../../services/graphqlClient';
import './ControlPresupuestal.css';

const QUERY = `query($proyecto_id: Int!) {
  controlPresupuestal(proyecto_id: $proyecto_id) {
    capitulos {
      id numero nombre subtotal_valor subtotal_presupuesto subtotal_ejecucion subtotal_delta
      actividades {
        id descripcion valor presupuesto material mano_obra todo_costo
        total_ejecucion delta completa
      }
    }
    resumen {
      total_valor total_presupuesto total_ejecucion total_delta
      actividades_total actividades_completas actividades_pendientes todas_completas
    }
  }
}`;

const MUT = `mutation($proyecto_id: Int!, $actividad_id: Int!, $material: Float, $mano_obra: Float, $todo_costo: Float) {
  actualizarControlPresupuestal(proyecto_id: $proyecto_id, actividad_id: $actividad_id, material: $material, mano_obra: $mano_obra, todo_costo: $todo_costo) { success message }
}`;

const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(n);
};

const parseInput = (s) => {
  if (s === '' || s === null || s === undefined) return null;
  const n = Number(s.toString().replace(/[^\d.-]/g, ''));
  return isNaN(n) ? null : n;
};

const ControlPresupuestal = ({ proyectoId }) => {
  const { error } = useNotification();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandidos, setExpandidos] = useState({});
  const [edits, setEdits] = useState({});

  const cargar = async () => {
    try {
      setLoading(true);
      const res = await client.query(QUERY, { proyecto_id: proyectoId });
      setData(res.controlPresupuestal);
      // Por defecto: todos los capítulos colapsados
      setExpandidos({});
    } catch (e) {
      error('Error al cargar: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [proyectoId]);

  const getVal = (act, campo) => {
    const key = `${act.id}.${campo}`;
    if (edits[key] !== undefined) return edits[key];
    const v = act[campo];
    return v === null || v === undefined ? '' : String(v);
  };

  const onChange = (actId, campo, valor) => {
    setEdits(prev => ({ ...prev, [`${actId}.${campo}`]: valor }));
  };

  const guardar = async (act) => {
    const material = parseInput(getVal(act, 'material'));
    const mano_obra = parseInput(getVal(act, 'mano_obra'));
    const todo_costo = parseInput(getVal(act, 'todo_costo'));

    // Buscar el valor PERSISTIDO en `data` (no en `act` porque viene de preview ya con los valores nuevos)
    let persisted = null;
    if (data) {
      for (const cap of data.capitulos) {
        const found = cap.actividades.find(x => x.id === act.id);
        if (found) { persisted = found; break; }
      }
    }

    // Si no cambió nada respecto a lo persistido, solo limpiar edits y salir
    if (persisted &&
        material === (persisted.material ?? null) &&
        mano_obra === (persisted.mano_obra ?? null) &&
        todo_costo === (persisted.todo_costo ?? null)) {
      setEdits(prev => {
        const n = { ...prev };
        delete n[`${act.id}.material`];
        delete n[`${act.id}.mano_obra`];
        delete n[`${act.id}.todo_costo`];
        return n;
      });
      return;
    }

    try {
      await client.mutate(MUT, {
        proyecto_id: proyectoId,
        actividad_id: act.id,
        material, mano_obra, todo_costo,
      });
      // Actualizar el estado local SIN recargar (evita scroll-to-top)
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          capitulos: prev.capitulos.map(cap => ({
            ...cap,
            actividades: cap.actividades.map(a =>
              a.id === act.id ? { ...a, material, mano_obra, todo_costo } : a
            ),
          })),
        };
      });
      setEdits(prev => {
        const n = { ...prev };
        delete n[`${act.id}.material`];
        delete n[`${act.id}.mano_obra`];
        delete n[`${act.id}.todo_costo`];
        return n;
      });
    } catch (e) {
      error('Error al guardar: ' + e.message);
    }
  };

  const toggleCap = (id) => setExpandidos(p => ({ ...p, [id]: !p[id] }));

  const preview = useMemo(() => {
    if (!data) return null;
    return data.capitulos.map(cap => {
      const acts = cap.actividades.map(a => {
        const m = parseInput(getVal(a, 'material'));
        const mo = parseInput(getVal(a, 'mano_obra'));
        const tc = parseInput(getVal(a, 'todo_costo'));
        const completa = m !== null && mo !== null && tc !== null;
        const ejec = completa ? (m + mo + tc) : null;
        const delta = completa ? (a.presupuesto - ejec) : null;
        return { ...a, material: m, mano_obra: mo, todo_costo: tc, total_ejecucion: ejec, delta, completa };
      });
      return { ...cap, actividades: acts };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, edits]);

  const resumenPreview = useMemo(() => {
    if (!preview) return null;
    let tv = 0, tp = 0, te = 0, td = 0, tot = 0, comp = 0;
    preview.forEach(c => c.actividades.forEach(a => {
      tv += a.valor; tp += a.presupuesto; tot += 1;
      if (a.completa) { te += a.total_ejecucion; td += a.delta; comp += 1; }
    }));
    return { total_valor: tv, total_presupuesto: tp, total_ejecucion: te, total_delta: td, actividades_total: tot, actividades_completas: comp, actividades_pendientes: tot - comp, todas_completas: tot > 0 && comp === tot };
  }, [preview]);

  if (loading) return <div className="tuviCP-loading">Cargando control presupuestal…</div>;
  if (!data) return <div className="tuviCP-loading">Sin datos.</div>;

  const utilClass = resumenPreview.todas_completas
    ? (resumenPreview.total_delta >= 0 ? 'pos' : 'neg')
    : 'pend';

  return (
    <div className="tuviCP-root">
      <div className="tuviCP-top">
        <div className="tuviCP-top-title">
          <h2>Control Presupuestal</h2>
          <p className="tuviCP-top-sub">Presupuesto = Valor / 1.15. Delta = Presupuesto − Ejecución.</p>
        </div>
        <div className="tuviCP-kpis">
          <div className="tuviCP-kpi">
            <span className="tuviCP-kpi-label">Valor Total</span>
            <span className="tuviCP-kpi-val">{fmt(resumenPreview.total_valor)}</span>
          </div>
          <div className="tuviCP-kpi">
            <span className="tuviCP-kpi-label">Presupuesto</span>
            <span className="tuviCP-kpi-val">{fmt(resumenPreview.total_presupuesto)}</span>
          </div>
          <div className="tuviCP-kpi">
            <span className="tuviCP-kpi-label">Ejecutado</span>
            <span className="tuviCP-kpi-val">
              {resumenPreview.todas_completas ? fmt(resumenPreview.total_ejecucion) : '—'}
            </span>
          </div>
          <div className={`tuviCP-kpi tuviCP-kpi-utility ${utilClass}`}>
            <span className="tuviCP-kpi-label">
              {resumenPreview.todas_completas ? (resumenPreview.total_delta >= 0 ? 'Utilidad' : 'Pérdida') : 'Utilidad'}
            </span>
            <span className="tuviCP-kpi-val">
              {resumenPreview.todas_completas
                ? fmt(resumenPreview.total_delta)
                : `Faltan ${resumenPreview.actividades_pendientes} de ${resumenPreview.actividades_total}`}
            </span>
          </div>
        </div>
      </div>

      {preview.map(cap => {
        const abierto = !!expandidos[cap.id];
        const compCount = cap.actividades.filter(a => a.completa).length;
        const totalEjecCap = cap.actividades.reduce((s, a) => s + (a.completa ? a.total_ejecucion : 0), 0);
        const totalDeltaCap = cap.actividades.reduce((s, a) => s + (a.completa ? a.delta : 0), 0);
        const todasCompCap = cap.actividades.length > 0 && compCount === cap.actividades.length;
        return (
          <div key={cap.id} className={`tuviCP-cap ${abierto ? 'tuviCP-cap-open' : ''}`}>
            <div className="tuviCP-cap-head" onClick={() => toggleCap(cap.id)}>
              <div className="tuviCP-cap-title">
                <span className="tuviCP-chev">{abierto ? '▼' : '▶'}</span>
                <span className="tuviCP-cap-num">{cap.numero || ''}</span>
                <strong>{cap.nombre}</strong>
                <span className="tuviCP-cap-count">{compCount}/{cap.actividades.length} completas</span>
              </div>
              <div className="tuviCP-cap-subs">
                <span>Valor: <strong>{fmt(cap.subtotal_valor)}</strong></span>
                <span>Ppto: <strong>{fmt(cap.subtotal_presupuesto)}</strong></span>
                {todasCompCap && (
                  <span className={totalDeltaCap >= 0 ? 'pos' : 'neg'}>
                    Δ: <strong>{fmt(totalDeltaCap)}</strong>
                  </span>
                )}
              </div>
            </div>
            {abierto && (
              <div className="tuviCP-cap-body">
                <table className="tuviCP-table">
                  <thead>
                    <tr>
                      <th className="tuviCP-col-desc">Actividad</th>
                      <th className="tuviCP-col-num">Valor</th>
                      <th className="tuviCP-col-num">Presupuesto</th>
                      <th className="tuviCP-col-input">Material</th>
                      <th className="tuviCP-col-input">Mano de Obra</th>
                      <th className="tuviCP-col-input">Todo Costo</th>
                      <th className="tuviCP-col-num">Total Ejec.</th>
                      <th className="tuviCP-col-num">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cap.actividades.map(a => (
                      <tr key={a.id} className={a.completa ? 'done' : ''}>
                        <td className="tuviCP-col-desc">{a.descripcion}</td>
                        <td className="tuviCP-col-num">{fmt(a.valor)}</td>
                        <td className="tuviCP-col-num">{fmt(a.presupuesto)}</td>
                        <td className="tuviCP-col-input">
                          <input type="number" step="any" value={getVal(a, 'material')}
                            onChange={(e) => onChange(a.id, 'material', e.target.value)}
                            onBlur={() => guardar(a)} placeholder="0" />
                        </td>
                        <td className="tuviCP-col-input">
                          <input type="number" step="any" value={getVal(a, 'mano_obra')}
                            onChange={(e) => onChange(a.id, 'mano_obra', e.target.value)}
                            onBlur={() => guardar(a)} placeholder="0" />
                        </td>
                        <td className="tuviCP-col-input">
                          <input type="number" step="any" value={getVal(a, 'todo_costo')}
                            onChange={(e) => onChange(a.id, 'todo_costo', e.target.value)}
                            onBlur={() => guardar(a)} placeholder="0" />
                        </td>
                        <td className="tuviCP-col-num">{a.completa ? fmt(a.total_ejecucion) : <span className="tuviCP-pend">—</span>}</td>
                        <td className={`tuviCP-col-num ${a.completa ? (a.delta >= 0 ? 'pos' : 'neg') : ''}`}>
                          {a.completa ? fmt(a.delta) : <span className="tuviCP-pend">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Subtotal</td>
                      <td className="tuviCP-col-num"><strong>{fmt(cap.subtotal_valor)}</strong></td>
                      <td className="tuviCP-col-num"><strong>{fmt(cap.subtotal_presupuesto)}</strong></td>
                      <td colSpan="3"></td>
                      <td className="tuviCP-col-num">{todasCompCap ? <strong>{fmt(totalEjecCap)}</strong> : '—'}</td>
                      <td className={`tuviCP-col-num ${todasCompCap ? (totalDeltaCap >= 0 ? 'pos' : 'neg') : ''}`}>
                        {todasCompCap ? <strong>{fmt(totalDeltaCap)}</strong> : '—'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ControlPresupuestal;
